'use client'

import { X } from 'lucide-react'
import { TRPCClientError } from '@trpc/client'

/**
 * Turns an unknown error (usually a tRPC mutation failure) into a
 * merchant-readable message. TRPCClientError messages are safe to show;
 * anything else collapses to a generic fallback.
 */
export function toErrorMessage(err: unknown): string {
  if (err instanceof TRPCClientError && err.message) return err.message
  return 'Something went wrong. Please try again.'
}

/**
 * Fixed-position dismissible error toast for surfacing mutation failures.
 * Render it unconditionally with a `string | null` message — it renders
 * nothing when the message is null.
 */
export function ErrorToast({
  message,
  onDismiss,
}: {
  message: string | null
  onDismiss: () => void
}) {
  if (!message) return null

  return (
    <div
      role="alert"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-start gap-3 w-[calc(100%-2rem)] max-w-md p-4 bg-red-50 border border-red-200 rounded-xl shadow-lg"
    >
      <p className="flex-1 text-sm text-red-800">{message}</p>
      <button
        onClick={onDismiss}
        aria-label="Dismiss error"
        className="text-red-400 hover:text-red-600 transition-colors shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
